// usePollWhileActive — a setInterval that stops when nobody is using the tab.
//
// Runs `fn` once on start (unless `runOnStart: false`), then every `intervalMs`
// while the activity gate says someone is here (visible + input within 15 min
// by default; see lib/activityGate.ts). While inactive it issues nothing. When
// the user comes back it refreshes at once if a tick came due while paused,
// then resumes the cadence — for an active user it is exactly the old
// setInterval. Built for the staff pollers (Aurora load workstream #4); the
// public sign page can reuse it as-is.
//
// `fn` is read through a ref, so passing a fresh closure each render does not
// restart the poll. Changing `enabled`, `intervalMs`, `runOnStart` or the watch
// thresholds does.

import { useEffect, useRef } from 'react';
import { createActivePoller, getActivityGate, type ActivityWatch } from '@/lib/activityGate';

export interface PollWhileActiveOptions {
  /** false = not polling at all (and no start-up run). Default true. */
  enabled?: boolean;
  /** Run immediately when (re)started, whatever the activity. Default true. */
  runOnStart?: boolean;
  /** Custom idle thresholds; default 15 min idle, hidden = idle at once. */
  watch?: ActivityWatch;
}

export function usePollWhileActive(
  fn: () => unknown,
  intervalMs: number,
  options: PollWhileActiveOptions = {},
): void {
  const fnRef = useRef(fn);
  fnRef.current = fn;
  const enabled = options.enabled ?? true;
  const runOnStart = options.runOnStart ?? true;
  const idleMs = options.watch?.idleMs;
  const hiddenGraceMs = options.watch?.hiddenGraceMs;

  useEffect(() => {
    if (!enabled) return;
    const watch: ActivityWatch = {};
    if (idleMs !== undefined) watch.idleMs = idleMs;
    if (hiddenGraceMs !== undefined) watch.hiddenGraceMs = hiddenGraceMs;
    const poller = createActivePoller({
      gate: getActivityGate(),
      run: () => fnRef.current(),
      intervalMs,
      runOnStart,
      watch,
    });
    return () => poller.stop();
  }, [enabled, intervalMs, runOnStart, idleMs, hiddenGraceMs]);
}
